/**
 * AddressAutocomplete Component
 * Google Places Autocomplete for address validation and selection
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Input } from '../ui/input';
import { MapPin, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AddressAutocomplete = ({
  value = '',
  onChange,
  onSelect,
  placeholder = 'Buscar dirección...',
  country = 'CO', // Colombia by default
  className = '',
  disabled = false,
  showValidation = true,
  'data-testid': testId = 'address-autocomplete'
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceTimer = useRef(null);

  // Update input when value prop changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions with debounce
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API_URL}/api/geolocation/places/autocomplete`, {
        params: {
          input: query,
          country: country
        }
      });

      if (response.data.predictions) {
        setSuggestions(response.data.predictions);
        setShowDropdown(true);
      } else {
        setSuggestions([]);
      }
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('no está configurado')) {
        setError('Google Maps no configurado');
      } else {
        setError('Error al buscar direcciones');
      }
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [country]);

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsValidated(false);
    setSelectedPlace(null);
    
    if (onChange) {
      onChange(newValue);
    }

    // Debounce API calls
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  // Handle place selection
  const handleSelectPlace = async (prediction) => {
    setInputValue(prediction.description);
    setSuggestions([]);
    setShowDropdown(false);
    setLoading(true);

    try {
      // Get place details including coordinates
      const response = await axios.get(`${API_URL}/api/geolocation/places/details`, {
        params: {
          place_id: prediction.place_id
        }
      });

      const placeDetails = response.data;
      setSelectedPlace(placeDetails);
      setIsValidated(true);

      if (onSelect) {
        onSelect({
          address: prediction.description,
          formatted_address: placeDetails.formatted_address,
          place_id: prediction.place_id,
          latitude: placeDetails.latitude,
          longitude: placeDetails.longitude,
          city: placeDetails.city,
          department: placeDetails.department,
          country: placeDetails.country,
          components: placeDetails.address_components
        });
      }

      if (onChange) {
        onChange(prediction.description);
      }
    } catch (err) {
      console.error('Error getting place details:', err);
      setError('Error al obtener detalles');
      setIsValidated(false);
    } finally {
      setLoading(false);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  return (
    <div className={cn("relative", className)}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "pl-10 pr-10",
            isValidated && showValidation && "border-green-500 focus-visible:ring-green-500",
            error && "border-red-300"
          )}
          data-testid={testId}
        />
        
        {/* Status icons */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {loading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          {!loading && isValidated && showValidation && (
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          )}
          {!loading && error && (
            <XCircle className="h-4 w-4 text-red-400" />
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <p className="text-xs text-red-500 mt-1">{error}</p>
      )}

      {/* Suggestions dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.map((prediction, index) => (
            <button
              key={prediction.place_id || index}
              type="button"
              className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b last:border-b-0 flex items-start gap-3 transition-colors"
              onClick={() => handleSelectPlace(prediction)}
              data-testid={`suggestion-${index}`}
            >
              <MapPin className="h-4 w-4 text-[#E63946] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {prediction.structured_formatting?.main_text || prediction.description.split(',')[0]}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {prediction.structured_formatting?.secondary_text || prediction.description}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Validated place info */}
      {selectedPlace && isValidated && showValidation && (
        <div className="mt-2 p-2 bg-green-50 rounded-lg border border-green-200">
          <p className="text-xs text-green-800 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Dirección validada
          </p>
          <p className="text-xs text-green-600 mt-1">
            {selectedPlace.latitude?.toFixed(6)}, {selectedPlace.longitude?.toFixed(6)}
          </p>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
